import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useUpcomingEvents } from '@/hooks/useEvents';
import { useHolidays } from '@/hooks/useHolidays';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';
import CalendarBottomSheet from '@/components/CalendarBottomSheet';
import type { Event, Holiday } from '@/types';

const WEEKDAYS = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ru: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
  pl: ['Nd', 'Pn', 'Wt', 'Sr', 'Cz', 'Pt', 'Sb'],
};

const getLocale = (language: 'en' | 'ru' | 'pl'): string => {
  const locales = { en: 'en-US', ru: 'ru-RU', pl: 'pl-PL' };
  return locales[language];
};

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface DayData {
  events: Event[];
  holidays: Holiday[];
}

export default function CalendarPage() {
  const { language } = useAppLanguage();
  const navigate = useNavigate();

  // Memoize today to avoid recreation on every render
  const today = useMemo(() => new Date(), []);

  // Month navigation state
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Bottom sheet state
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedDayData, setSelectedDayData] = useState<DayData>({ events: [], holidays: [] });

  // Fetch data
  const { data: events = [] } = useUpcomingEvents(language);
  const { data: holidays = [] } = useHolidays();

  // Navigation constraints
  const canGoBack = useMemo(() => {
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return currentMonth.getTime() > currentMonthStart.getTime();
  }, [currentMonth, today]);

  const canGoForward = useMemo(() => {
    const maxMonth = new Date(today.getFullYear(), today.getMonth() + 12, 1);
    return currentMonth.getTime() < maxMonth.getTime();
  }, [currentMonth, today]);

  const goToPreviousMonth = () => {
    if (canGoBack) {
      setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  };

  const goToNextMonth = () => {
    if (canGoForward) {
      setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  };

  // Create lookup map: "YYYY-MM-DD" -> { events: [], holidays: [] }
  const eventsByDate = useMemo(() => {
    const map = new Map<string, DayData>();

    events.forEach(event => {
      const dateKey = event.startDate.split('T')[0];
      const existing = map.get(dateKey) || { events: [], holidays: [] };
      existing.events.push(event);
      map.set(dateKey, existing);
    });

    holidays.forEach(holiday => {
      const dateKey = holiday.date;
      const existing = map.get(dateKey) || { events: [], holidays: [] };
      existing.holidays.push(holiday);
      map.set(dateKey, existing);
    });

    return map;
  }, [events, holidays]);

  // Calendar grid calculation
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const days = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    return [
      ...Array.from({ length: firstDay }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
  }, [currentMonth]);

  const monthName = currentMonth.toLocaleDateString(getLocale(language), {
    month: 'long',
    year: 'numeric',
  });

  const handleDayClick = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateKey = formatDateKey(date);
    const data = eventsByDate.get(dateKey);

    if (data && (data.events.length > 0 || data.holidays.length > 0)) {
      setSelectedDay(date);
      setSelectedDayData(data);
    }
  };

  const handleEventClick = (eventId: string) => {
    setSelectedDay(null);
    navigate(`/events/${eventId}`, { state: { fromApp: true } });
  };

  const handleCloseSheet = () => {
    setSelectedDay(null);
  };

  const isToday = (day: number) => {
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const getDayIndicators = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateKey = formatDateKey(date);
    const data = eventsByDate.get(dateKey);
    return {
      hasEvents: (data?.events.length || 0) > 0,
      hasHolidays: (data?.holidays.length || 0) > 0,
    };
  };

  return (
    <DetailPageLayout>
      <DetailPageHeader title={getTranslation('calendar', language)} />

      <div style={styles.content}>
        {/* Month Navigation */}
        <div style={styles.monthNavigation}>
          <button
            onClick={goToPreviousMonth}
            disabled={!canGoBack}
            style={{
              ...styles.navButton,
              opacity: canGoBack ? 1 : 0.3,
              cursor: canGoBack ? 'pointer' : 'default',
            }}
          >
            <ChevronLeft size={24} color={Colors.text} />
          </button>

          <div style={styles.monthName}>{monthName}</div>

          <button
            onClick={goToNextMonth}
            disabled={!canGoForward}
            style={{
              ...styles.navButton,
              opacity: canGoForward ? 1 : 0.3,
              cursor: canGoForward ? 'pointer' : 'default',
            }}
          >
            <ChevronRight size={24} color={Colors.text} />
          </button>
        </div>

        {/* Weekday Headers */}
        <div style={styles.weekDays}>
          {WEEKDAYS[language].map((day) => (
            <div key={day} style={styles.weekDay}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div style={styles.daysGrid}>
          {days.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} style={styles.emptyCell} />;
            }

            const { hasEvents, hasHolidays } = getDayIndicators(day);
            const isTodayCell = isToday(day);
            const hasData = hasEvents || hasHolidays;

            return (
              <button
                key={day}
                onClick={() => handleDayClick(day)}
                style={{
                  ...styles.dayCell,
                  ...(hasEvents && hasHolidays ? styles.eventAndHolidayCell : {}),
                  ...(hasEvents && !hasHolidays ? styles.eventCell : {}),
                  ...(!hasEvents && hasHolidays ? styles.holidayCell : {}),
                  ...(isTodayCell ? styles.todayCell : {}),
                  ...(hasData ? styles.clickableCell : {}),
                }}
              >
                <span style={styles.dayNumber}>{day}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ height: '40px' }} />

      {/* Bottom Sheet */}
      <CalendarBottomSheet
        isOpen={selectedDay !== null}
        onClose={handleCloseSheet}
        selectedDate={selectedDay}
        events={selectedDayData.events}
        holidays={selectedDayData.holidays}
        language={language}
        onEventClick={handleEventClick}
      />
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    padding: '24px',
  } as React.CSSProperties,

  monthNavigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  } as React.CSSProperties,

  navButton: {
    background: 'none',
    border: 'none',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  monthName: {
    fontSize: '20px',
    fontWeight: '700',
    color: Colors.text,
    textAlign: 'center' as const,
    textTransform: 'capitalize' as const,
  } as React.CSSProperties,

  weekDays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
    marginBottom: '16px',
  } as React.CSSProperties,

  weekDay: {
    textAlign: 'center' as const,
    fontWeight: '600',
    color: Colors.textSecondary,
    fontSize: '12px',
  } as React.CSSProperties,

  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
  } as React.CSSProperties,

  emptyCell: {
    aspectRatio: '1',
  } as React.CSSProperties,

  dayCell: {
    aspectRatio: '1',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.surface,
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.text,
    border: 'none',
    cursor: 'default',
    padding: '4px',
  } as React.CSSProperties,

  todayCell: {
    backgroundColor: Colors.primary,
    color: Colors.surface,
  } as React.CSSProperties,

  clickableCell: {
    cursor: 'pointer',
  } as React.CSSProperties,

  dayNumber: {
    lineHeight: '1',
  } as React.CSSProperties,

  eventCell: {
    backgroundColor: 'rgba(74, 124, 155, 0.25)',
  } as React.CSSProperties,

  holidayCell: {
    backgroundColor: 'rgba(193, 154, 107, 0.25)',
  } as React.CSSProperties,

  eventAndHolidayCell: {
    background: 'linear-gradient(135deg, rgba(74, 124, 155, 0.3) 50%, rgba(193, 154, 107, 0.3) 50%)',
  } as React.CSSProperties,
};
